// src/oura/merchant/PackNote.tsx
//
// The note a batch of questions came with: why we are asking, a table of the
// pages involved, a screenshot. Staff write it in the composer. The merchant
// reads it in the question drawer's "{AM}'s note" fold; staff read the same
// note, read-only, in the pack drawer (PackSheet). `PackNoteBody` renders the
// blocks and nothing around them, so each host puts its own fold round it.

import { useState } from "react";
import { createPortal } from "react-dom";
import { XIcon } from "lucide-react";
import type { Pack } from "@service/aeo/clientReview";
import { FOCUS_RING, SURFACE, TYPE, useEscapeLayer } from "@oura/ui";
import { cn } from "../lib/cn";
import { noteBlocks } from "./asks";

/**
 * The note's sections, tables and screenshots, stacked. Renders nothing for
 * a pack with no note. Inside a drawer fold the blocks sit on the grey inner
 * ground, so they read as the note's parts rather than as more drawer blocks.
 */
export function PackNoteBody({ pack, className }: { pack: Pack; className?: string }) {
    const blocks = noteBlocks(pack);
    if (blocks.length === 0) return null;
    return (
        <div className={cn("space-y-3", className)}>
            {blocks.map((block) => {
                if (block.type === "section") {
                    return (
                        <section key={block.id} className={SURFACE.inner}>
                            <h4 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>{block.heading}</h4>
                            <div className={cn(TYPE.tb, "mt-1 space-y-2 leading-6 text-gray-600")}>
                                {block.body.split(/\n{2,}/).map((p, i) => (
                                    <p key={i} className="m-0">
                                        {p}
                                    </p>
                                ))}
                            </div>
                        </section>
                    );
                }
                if (block.type === "table") {
                    return (
                        // The inner ground without its padding: the table runs to the edges.
                        <section key={block.id} className="overflow-hidden rounded-xl bg-gray-50">
                            {block.title && <h4 className={cn(TYPE.tb, "m-0 px-3 pt-2.5 font-semibold text-gray-900")}>{block.title}</h4>}
                            <div className="overflow-x-auto px-3 pb-1">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-300">
                                            {block.columns.map((c, i) => (
                                                <th key={i} className={cn(TYPE.lab, "py-2 pr-4 text-left")}>
                                                    {c}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {block.rows.map((row, i) => (
                                            <tr key={i} className="border-t border-gray-200 first:border-t-0">
                                                {row.map((cell, j) => (
                                                    <td key={j} className={cn(TYPE.tb, "py-2.5 pr-4", j === 0 ? "font-medium text-gray-900" : "text-gray-600")}>
                                                        {cell}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    );
                }
                return <Evidence key={block.id} caption={block.caption} label={block.label} fileUrl={block.fileUrl ?? null} />;
            })}
        </div>
    );
}

/** A screenshot, which opens full size. A block with no image yet shows its label on a striped box. */
function Evidence({ caption, label, fileUrl }: { caption: string; label: string; fileUrl: string | null }) {
    const [full, setFull] = useState(false);
    const name = label || caption || "Screenshot";
    return (
        <figure className={cn(SURFACE.inner, "m-0")}>
            <h4 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>{caption || "Screenshot"}</h4>
            {fileUrl ? (
                <button type="button" onClick={() => setFull(true)} aria-label={`Open ${name} full size`} className={cn("mt-2 block w-full rounded-xl", FOCUS_RING)}>
                    <img src={fileUrl} alt={name} className="max-h-64 w-full rounded-xl object-contain ring-1 ring-black/[0.06]" />
                </button>
            ) : (
                <div className={cn(TYPE.tc, "mt-2 flex h-28 items-end rounded-xl bg-[repeating-linear-gradient(135deg,#e5e7eb_0_8px,#f3f4f6_8px_16px)] px-3 py-2 text-gray-500")}>
                    {label}
                </div>
            )}
            {fileUrl && label && <figcaption className={cn(TYPE.tm, "mt-1.5 text-gray-500")}>{label}</figcaption>}
            {full && fileUrl && <Lightbox src={fileUrl} alt={name} onClose={() => setFull(false)} />}
        </figure>
    );
}

/**
 * The image full size, over the drawer. It joins the escape stack, so Escape
 * closes it and leaves the drawer open behind it.
 */
function Lightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
    useEscapeLayer(onClose, true);
    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label={alt}
            onClick={onClose}
            // One control, so Tab stays on it; and the key stops here rather
            // than reaching the drawer's own focus trap underneath.
            onKeyDown={(e) => {
                if (e.key !== "Tab") return;
                e.preventDefault();
                e.stopPropagation();
            }}
            className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4"
        >
            <img src={src} alt={alt} className="max-h-full max-w-full rounded-lg object-contain" />
            <button
                type="button"
                autoFocus
                onClick={onClose}
                aria-label="Close"
                className={cn("absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-white text-gray-900", FOCUS_RING)}
            >
                <XIcon aria-hidden size={18} />
            </button>
        </div>,
        document.body,
    );
}
